import { useState, type JSX, type ReactNode } from "react";
import type { ConsoleUser } from "../data.js";
import { Icon, PageHead, Seg } from "../ui.js";

type SettingsTab = "manage" | "notify" | "display" | "data" | "about";

/** Management lives in the classic layout; these rows deep-link into it (main.tsx reads ?classic&page=<id>). */
const MANAGE: Array<{ page: string; label: string; text: string; adminOnly?: boolean }> = [
  { page: "admin", label: "管理员", text: "管理员控制台 · 全站用户、采集器与设备概览", adminOnly: true },
  { page: "devices", label: "设备配对", text: "配对或解绑常驻显示的 Android 屏" },
  { page: "collectors", label: "采集器", text: "本机采集器接入与令牌" },
  { page: "tokens", label: "数据 API", text: "创建与吊销读取用量数据的 API Token" },
];
const classicUrl = (page: string): string => `/app?classic&page=${page}`;

function Row({ label, children, action }: { label: string; children: ReactNode; action?: ReactNode }): JSX.Element {
  return <div className="cx-srow"><span className="k">{label}</span>{children}{action ?? <span />}</div>;
}

function Plain({ title, rows }: { title: string; rows: Array<[string, ReactNode, ReactNode?]> }): JSX.Element {
  return (
    <section className="cx-card neutral cx-plain">
      <header className="cx-card-head small"><h2>{title}</h2></header>
      {rows.map(([label, value, action]) => <Row key={label} label={label} action={action}><div className="cx-field">{value}</div></Row>)}
    </section>
  );
}

/** `manage` is false on a paired display (read-only display token): no management entry may render there. */
export function Settings({ user, manage, onSync }: { user: ConsoleUser; manage: boolean; onSync: () => void }): JSX.Element {
  const [tab, setTab] = useState<SettingsTab>(manage ? "manage" : "notify");
  const tabs: ReadonlyArray<readonly [SettingsTab, string]> = [
    ...(manage ? [["manage", "管理"] as const] : []),
    ["notify", "通知设置"], ["display", "显示偏好"], ["data", "数据管理"], ["about", "关于"],
  ];
  const avatar = user.avatarVersion ? `/v1/profile/avatar?v=${encodeURIComponent(user.avatarVersion)}` : null;
  const now = Math.floor(Date.now() / 1000);

  return (
    <>
      <PageHead title="设置" subtitle="管理设备、采集器、通知和显示选项" right={<button className="cx-btn blue solid" onClick={onSync}><Icon name="sync" />同步配置</button>} />
      <div className="cx-settings-bar">
        <Seg items={tabs} value={tab} onChange={setTab} />
      </div>

      {manage && tab === "manage" && <Plain title="管理" rows={MANAGE.filter((item) => !item.adminOnly || user.isAdmin).map((item) => [
        item.label, item.text, <button key={item.page} className="cx-btn dark wide" onClick={() => window.location.assign(classicUrl(item.page))}>打开</button>,
      ])} />}

      {tab === "notify" && <Plain title="通知设置" rows={[["配额提醒", "尚未接入通知渠道。配额接近上限时的推送在规划中。"]]} />}

      {tab === "display" && <Plain title="显示偏好" rows={[
        ["全屏看板", "浏览器全屏后可常驻显示", <button key="fs" className="cx-btn dark wide" onClick={() => void document.documentElement.requestFullscreen?.()}>进入全屏</button>],
        ["横屏锁定", "适合挂在墙上或桌面的横屏设备", <button key="lock" className="cx-btn dark wide" onClick={() => { const orientation = screen.orientation as ScreenOrientation & { lock?: (value: "landscape") => Promise<void> }; void orientation.lock?.("landscape"); }}>锁定横屏</button>],
        ["刷新频率", "自动刷新 120 秒，也可手动刷新"],
      ]} />}

      {tab === "data" && <Plain title="数据管理" rows={[
        ["账号", `${user.displayName} · ${user.email}${user.isAdmin ? " · 管理员" : ""}`],
        ["头像", avatar ? <img src={avatar} alt="" className="cx-mini-avatar" /> : "未设置"],
        ["导出", "最近 30 天聚合数据（JSON）", <button key="export" className="cx-btn dark wide" onClick={() => window.open(`/v1/analytics?from=${now - 30 * 86400}&to=${now}&bucket=day`, "_blank", "noopener")}>导出</button>],
      ]} />}

      {tab === "about" && <Plain title="关于" rows={[
        ["产品", "UsageHub · AI 用量看板"],
        ["接口", `${typeof window === "undefined" ? "" : window.location.origin}/v1`],
        ["数据口径", "配额来自 Claude 官方 /usage（claude -p）/ Codex 官方 app-server，Token 与费用来自本机 ccusage"],
        ["费用说明", "费用为 API 等价估算，非账单金额"],
      ]} />}
    </>
  );
}
